import React from "react";

import { UseMutationResult, useMutation } from "react-query";

import LocaleSelector from "components/LocaleSelector";
import { useAPI } from "libs/api";
import { useAuthentication } from "libs/authentication";
import { useI18n } from "libs/i18n";

import { I18nLocaleSelectorProps, SupportedLocale } from "./types";

// ---------- Types ----------
// TODO: Extract this types later linked to steady migration to TypeScript
type UserUpdatePayload = {
  language: string;
};

type UsersApi = {
  update: (_data: UserUpdatePayload) => Promise<unknown>;
};

type ApiClient = {
  users: UsersApi;
};

type I18nContext = {
  allowedLocales: SupportedLocale[];
  locale: SupportedLocale;
  setLocale: (_locale: string) => void;
};

type AuthenticationContext = {
  isAuthenticated: boolean;
};

// ---------- Component ----------

/**
 * A specialised component that uses the generic LocaleSelector component
 * to display a dropdown selector for the locale.
 * Linked to the i18n lib and provides a way to override props
 * to not use the i18n global system.
 */
const I18nLocaleSelector: React.FC<I18nLocaleSelectorProps> = ({
  availableLocales = undefined,
  selectedLocale = undefined,
  onChange = undefined,
  displayFlag = false,
  displayNativeName = true,
  shouldSaveLocaleOnChange = true,
}: I18nLocaleSelectorProps): React.ReactElement => {
  const api: ApiClient = useAPI("WeezPay") as ApiClient;
  const { isAuthenticated }: AuthenticationContext = useAuthentication() as AuthenticationContext;
  const {
    allowedLocales: i18nAllowedLocales,
    locale: i18nLocale,
    setLocale: i18nSetLocale,
  }: I18nContext = useI18n() as I18nContext;

  const userUpdateMutation: UseMutationResult<unknown, unknown, UserUpdatePayload> = useMutation<
    unknown,
    unknown,
    UserUpdatePayload
  >(
    (data: UserUpdatePayload): Promise<unknown> => api.users.update(data),
  );

  const hasLocaleChanged = React.useCallback(
    (newLocale: string): boolean => i18nLocale !== newLocale,
    [i18nLocale],
  );

  const saveLocaleToServer = React.useCallback(
    async (newLocale: string): Promise<void> => {
      if (shouldSaveLocaleOnChange && hasLocaleChanged(newLocale) && isAuthenticated) {
        try {
          await userUpdateMutation.mutateAsync({ language: newLocale });
        } catch {
          // eslint-disable-next-line no-console
          console.error("Save locale to the user preferences failed.");
        }
      }
    },
    [shouldSaveLocaleOnChange, hasLocaleChanged, isAuthenticated, userUpdateMutation],
  );

  const onChangeValue = React.useCallback(
    async (value: string): Promise<void> => {
      if (onChange) {
        onChange(value);
      }
      i18nSetLocale(value);
      await saveLocaleToServer(value);
    },
    [onChange, i18nSetLocale, saveLocaleToServer],
  );

  const resolvedAvailableLocales: SupportedLocale[] = availableLocales ?? i18nAllowedLocales;
  const resolvedSelectedLocale: SupportedLocale = selectedLocale ?? i18nLocale;

  return (
    <LocaleSelector
      availableLocales={resolvedAvailableLocales}
      selectedLocale={resolvedSelectedLocale}
      onChange={onChangeValue}
      displayFlag={displayFlag}
      displayNativeName={displayNativeName}
    />
  );
};

export default I18nLocaleSelector;
